(() => {
  'use strict';
  if (customElements.get('ps-glycerol-product')) return;

  class PSGlycerolProduct extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.config = JSON.parse(this.querySelector('[data-gly-config]').textContent);
      this.form = this.querySelector('form');
      this.radios = [...this.form.querySelectorAll('input[name="quantity"]')];
      this.buttons = [...this.querySelectorAll('[data-gly-add]')];
      this.error = this.querySelector('[data-gly-error]');
      this.sticky = this.querySelector('.ps-gly__sticky');
      this.money = new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.config.currency, currencyDisplay: 'narrowSymbol' });
      this.pending = false;
      const requestedQuantity = new URLSearchParams(window.location.search).get('ps_quantity');
      const onProduct = window.location.pathname.replace(/\/$/, '').endsWith('/products/' + this.config.productHandle);
      const requestedRadio = onProduct && this.radios.find(radio => radio.value === requestedQuantity && !radio.disabled);
      if (requestedRadio) requestedRadio.checked = true;
      this.form.addEventListener('change', () => this.update(), options);
      this.form.addEventListener('submit', event => this.add(event), options);
      this.querySelectorAll('a[href="#ps-gly-label"]').forEach(link => {
        link.addEventListener('click', () => { this.querySelector('#ps-gly-label').open = true; }, options);
      });
      this.gallery = this.querySelector('.ps-gly__gallery');
      this.thumbnails = [...this.querySelectorAll('[data-gly-image]')];
      this.thumbnails.forEach(button => button.addEventListener('click', () => {
        const index = Number(button.dataset.glyImage);
        this.gallery.scrollTo({ left: this.gallery.clientWidth * index, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options));
      this.gallery.addEventListener('scroll', () => {
        const index = Math.round(this.gallery.scrollLeft / this.gallery.clientWidth);
        this.thumbnails.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      }, { ...options, passive: true });
      this.checkSticky = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const mainButton = this.form.querySelector('[data-gly-add]');
          const focused = document.activeElement;
          const editing = focused && focused.matches('input:not([type="radio"]), textarea, select');
          this.sticky.hidden = mainButton.getBoundingClientRect().bottom > 0 || matchMedia('(min-width: 990px)').matches || editing;
        });
      };
      window.addEventListener('scroll', this.checkSticky, { ...options, passive: true });
      window.addEventListener('resize', this.checkSticky, options);
      document.addEventListener('focusin', this.checkSticky, options);
      document.addEventListener('focusout', this.checkSticky, options);
      this.classList.add('ps-gly--ready');
      this.update();
      this.checkSticky();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
    }

    selection() {
      const quantity = Number(this.form.querySelector('input[name="quantity"]:checked')?.value);
      return this.config.offers.find(offer => offer.quantity === quantity);
    }

    update() {
      const offer = this.selection();
      const priceKnown = offer && offer.verified !== false && Number.isInteger(offer.unitPrice);
      const valid = priceKnown && this.config.available && offer.quantity <= this.config.maxQuantity;
      const quantity = offer?.quantity || 1;
      const bottles = `${quantity} ${quantity === 1 ? 'bottle' : 'bottles'}`;
      const unitPrice = this.money.format((offer?.unitPrice || 0) / 100);
      const total = this.money.format((offer?.unitPrice || 0) * quantity / 100);
      this.querySelector('[data-gly-selection]').textContent = valid ? `${bottles} selected · ${unitPrice} per bottle · ${total} total` : !priceKnown ? 'Bundle price unavailable' : 'Currently unavailable';
      this.querySelector('[data-gly-sticky-quantity]').textContent = priceKnown ? `${bottles} · ${total} total` : bottles;
      this.querySelector('[data-gly-sticky-price]').textContent = priceKnown ? `${unitPrice} per bottle` : 'Bundle price unavailable';
      this.buttons.forEach(button => {
        button.disabled = !valid || this.pending;
        button.setAttribute('aria-busy', String(this.pending));
        button.setAttribute('aria-label', valid ? `Add ${bottles} to cart at ${unitPrice} per bottle, ${total} total` : !priceKnown ? 'Bundle price unavailable' : 'Sold out');
        button.querySelector('[data-gly-add-label]').textContent = this.pending ? 'Adding…' : !priceKnown ? 'Bundle price unavailable' : !valid ? 'Sold out' : button.closest('.ps-gly__sticky') ? `Add to cart · ${total}` : `Add ${bottles} · ${total} total`;
      });
    }

    async add(event) {
      event.preventDefault();
      event.stopPropagation(); // Avoid the exported theme's legacy body submit handler.
      const offer = this.selection();
      if (this.pending || !this.config.available || !offer || offer.verified === false || !Number.isInteger(offer.unitPrice) || offer.quantity > this.config.maxQuantity) return;
      const body = new FormData(this.form);
      body.set('id', String(this.config.variantId));
      body.set('quantity', String(offer.quantity));
      this.pending = true;
      this.error.hidden = true;
      this.radios.forEach(radio => { radio.disabled = true; });
      this.update();
      let confirmed = false;
      try {
        const response = await fetch(this.form.action.replace(/\/add\/?$/, '/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body });
        const result = await response.json();
        if (!response.ok || result.status >= 400) {
          this.showError(typeof result.description === 'string' ? result.description : 'This quantity could not be added. Please check availability and try again.');
          return;
        }
        confirmed = true;
        // The old theme restores the PDP scroll offset even after navigating
        // to cart. Start at the cart heading after this new purchase flow.
        try { localStorage.removeItem('vcl_scroll_pos'); } catch (_) { /* Storage can be unavailable. */ }
        window.location.assign(this.config.cartUrl);
      } catch (error) {
        this.showError(confirmed ? 'Your bottles were added. Open your cart to continue.' : 'We could not confirm the cart update. Check your cart before trying again.');
      } finally {
        if (!confirmed) {
          this.pending = false;
          this.radios.forEach(radio => { radio.disabled = !this.config.available || Number(radio.value) > this.config.maxQuantity || this.config.offers.find(item => item.quantity === Number(radio.value))?.verified === false; });
          this.update();
        }
      }
    }

    showError(message) {
      this.error.replaceChildren(document.createTextNode(message), document.createElement('br'));
      const link = document.createElement('a');
      link.href = this.config.cartUrl;
      link.textContent = 'View your cart';
      this.error.append(link);
      this.error.hidden = false;
      // The same error is reachable after submitting from the bottom bar.
      this.error.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }
  customElements.define('ps-glycerol-product', PSGlycerolProduct);
})();
